import React, { useState, useEffect } from "react";
import { C, PAYMENT_METHODS, getPerms } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, ReportsTab, useToast
} from "./erp-shared";
import { getTransactions, createTransaction, getBudgets, createBudget } from "./supabaseClient";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from "recharts";
import {
  Wallet, DollarSign, TrendingUp, TrendingDown, Plus, Search,
  CheckCircle, Clock, FileText, Calendar, Target,
  CreditCard, Receipt, Building,
  BarChart3, PieChart as PieIcon,
  Star, AlertCircle, RefreshCw
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Finance Page — المالية
   ═════════════════════════════════════════════ */
export default function FinancePage({ user }) {
  const [tab, setTab] = useState("overview");
  const perms = getPerms(user);

  // مصروفات حيّة لمصدر التقرير الشهري (module=finance · direction=expense) — لا mock.
  const [expenseRows, setExpenseRows] = useState([]);
  useEffect(() => {
    let alive = true;
    getTransactions("finance", "expense")
      .then((rows) => {
        if (!alive) return;
        setExpenseRows((rows || []).map((r, i) => ({
          number: `م-${String(i + 1).padStart(3, "0")}`,
          date: r.txn_date || "",
          department: r.department || "",
          description: r.description || "",
          amount: Number(r.amount) || 0,
        })));
      })
      .catch(() => { if (alive) setExpenseRows([]); });
    return () => { alive = false; };
  }, []);

  // إيرادات حيّة لمصدر التقرير الشهري (module=finance · direction=income) — لا mock.
  const [incomeRows, setIncomeRows] = useState([]);
  useEffect(() => {
    let alive = true;
    getTransactions("finance", "income")
      .then((rows) => {
        if (!alive) return;
        setIncomeRows((rows || []).map((r, i) => ({
          number: `إ-${String(i + 1).padStart(3, "0")}`,
          date: r.txn_date || "",
          type: r.txn_type || "",
          donor: r.beneficiary || "",
          amount: Number(r.amount) || 0,
        })));
      })
      .catch(() => { if (alive) setIncomeRows([]); });
    return () => { alive = false; };
  }, []);

  // ميزانيات الأقسام الحيّة لمصدر التقرير الشهري — لا mock.
  const [budgetRows, setBudgetRows] = useState([]);
  useEffect(() => {
    getBudgets()
      .then((rows) => setBudgetRows((rows || []).map(mapBudgetRow)))
      .catch(() => setBudgetRows([]));
  }, []);

  const tabs = [
    { key: "overview", label: "نظرة عامة", icon: Wallet },
    { key: "income", label: "الإيرادات", icon: TrendingUp },
    { key: "expenses", label: "المصروفات", icon: TrendingDown },
    { key: "budgets", label: "الميزانيات", icon: Target },
    { key: "accounts", label: "الحسابات", icon: Building },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  const money = (v) => (typeof v === "number" ? v.toLocaleString("ar-SA") : (v ?? "—"));
  const reportSources = [
    {
      label: "الإيرادات", mode: "monthly", dateField: "date", rows: incomeRows,
      columns: [
        { key: "number", label: "الرقم" },
        { key: "date", label: "التاريخ" },
        { key: "type", label: "النوع" },
        { key: "donor", label: "المصدر" },
        { key: "amount", label: "المبلغ", format: money },
      ],
    },
    {
      label: "المصروفات", mode: "monthly", dateField: "date", rows: expenseRows,
      columns: [
        { key: "number", label: "الرقم" },
        { key: "date", label: "التاريخ" },
        { key: "department", label: "القسم" },
        { key: "description", label: "البيان" },
        { key: "amount", label: "المبلغ", format: money },
      ],
    },
    { label: "ميزانيات الأقسام", mode: "cumulative", rows: budgetRows },
  ];

  return (
    <div>
      <ModuleHeader icon={Wallet} title="المالية" subtitle="إدارة الإيرادات والمصروفات والميزانيات والحسابات">
        <MonthlyReportButton moduleTitle="المالية" sources={reportSources} />
      </ModuleHeader>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview" && <OverviewTab />}
      {tab === "income" && <IncomeTab canEdit={perms.canEdit} />}
      {tab === "expenses" && <ExpensesTab canEdit={perms.canEdit} />}
      {tab === "budgets" && <BudgetsTab canEdit={perms.canEdit} />}
      {tab === "accounts" && <AccountsTab />}
      {tab === "meetings" && <MeetingsTab departmentName="المالية" module="finance" />}
      {tab === "reports" && <ReportsTab departmentName="المالية" />}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Overview Tab — نظرة عامة مالية
   ───────────────────────────────────────────── */
function OverviewTab() {
  const totalIncome = MONTHLY_DATA.reduce((s, m) => s + m.income, 0);
  const totalExpense = MONTHLY_DATA.reduce((s, m) => s + m.expense, 0);
  const balance = totalIncome - totalExpense;

  return (
    <div style={{ marginTop: 16 }}>
      <style>{`
        @media (max-width: 768px) {
          .finance-charts { flex-direction: column !important; }
          .finance-charts > div { min-width: 100% !important; }
        }
      `}</style>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الإيرادات" value={`${(totalIncome / 1000000).toFixed(2)}M`} icon={TrendingUp} color={C.success} />
        <KPICard title="إجمالي المصروفات" value={`${(totalExpense / 1000000).toFixed(2)}M`} icon={TrendingDown} color={C.danger} />
        <KPICard title="صافي الرصيد" value={`${(balance / 1000).toFixed(0)}K`} icon={DollarSign} color={C.teal} />
        <KPICard title="التبرعات" value="0" icon={CreditCard} color={C.accent} />
        <KPICard title="المعاملات" value="0" icon={Receipt} color="#7c3aed" />
        <KPICard title="الحسابات البنكية" value="0" icon={Building} color={C.primary} />
      </div>

      {/* Charts */}
      <div className="finance-charts" style={styles.chartsRow}>
        <Card style={{ padding: 20, flex: 1, minWidth: 300 }}>
          <h4 style={styles.chartTitle}><BarChart3 size={18} color={C.teal} /> الإيرادات والمصروفات الشهرية</h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={MONTHLY_DATA} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }} formatter={v => [`${v.toLocaleString()} ريال`]} />
              <Bar dataKey="income" name="الإيرادات" fill={C.success} radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="المصروفات" fill={C.danger} radius={[4, 4, 0, 0]} opacity={0.7} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={{ padding: 20, flex: 1, minWidth: 300 }}>
          <h4 style={styles.chartTitle}><PieIcon size={18} color="#7c3aed" /> توزيع المصروفات</h4>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={EXPENSE_DIST} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" paddingAngle={3}>
                {EXPENSE_DIST.map((e, i) => <Cell key={i} fill={EXPENSE_COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }} formatter={v => [`${v.toLocaleString()} ريال`]} />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {EXPENSE_DIST.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: EXPENSE_COLORS[i] }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Income trend */}
      <Card style={{ padding: 20, marginTop: 16 }}>
        <h4 style={styles.chartTitle}><TrendingUp size={18} color={C.success} /> تطور الإيرادات الشهرية</h4>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={MONTHLY_DATA}>
            <defs>
              <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={C.success} stopOpacity={0.2} />
                <stop offset="95%" stopColor={C.success} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
            <YAxis fontSize={11} tick={{ fill: C.textLight }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ borderRadius: 8, fontFamily: "'Readex Pro'" }} formatter={v => [`${v.toLocaleString()} ريال`]} />
            <Area type="monotone" dataKey="income" stroke={C.success} fill="url(#incGrad)" strokeWidth={2.5} name="الإيرادات" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Income Tab — الإيرادات
   ───────────────────────────────────────────── */
const INITIAL_INCOME = { type: "", date: "", donor: "", amount: "", method: "", description: "" };

function IncomeTab({ canEdit }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_INCOME);
  const [showToast, ToastView] = useToast();

  const INCOME_TYPES = ["تبرع نقدي","تبرع عيني","وقف","زكاة","صدقة","كفالة","منحة حكومية","إيراد استثماري"];

  // تحويل صف Supabase (module=finance · direction=income) إلى شكل الواجهة.
  // type→txn_type · donor→beneficiary · method→payment_method (الإرسال فقط).
  const mapRow = (row, i) => ({
    id: row.id,
    number: `إ-${String(i + 1).padStart(3, "0")}`,
    date: row.txn_date || "",
    type: row.txn_type || "",
    donor: row.beneficiary || "",
    amount: Number(row.amount) || 0,
    method: row.payment_method || "",
    status: row.status || "معتمد",
    description: row.description || "",
  });

  async function loadData() {
    setLoading(true);
    setLoadError("");
    try {
      const rows = await getTransactions("finance", "income");
      setData((rows || []).map(mapRow));
    } catch (e) {
      setLoadError(e?.message || "تعذّر جلب الإيرادات");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave() {
    if (!form.type || !form.date || !form.donor || !form.amount || !form.method) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    try {
      await createTransaction({
        module: "finance",
        direction: "income",
        txn_date: form.date,
        txn_type: form.type,
        beneficiary: form.donor,
        amount: Number(form.amount) || 0,
        payment_method: form.method,
        description: form.description || "",
        status: "معتمد",
      });
      setForm(INITIAL_INCOME);
      setShowAdd(false);
      showToast("تم تسجيل الإيراد");
      loadData();
    } catch (e) {
      showToast(e?.message || "تعذّر حفظ الإيراد", "error");
    }
  }

  const filtered = data.filter(d => {
    const matchSearch = d.description.includes(search) || d.donor.includes(search);
    const matchType = filterType === "all" || d.type === filterType;
    return matchSearch && matchType;
  });

  const total = filtered.reduce((s, d) => s + d.amount, 0);
  const maxDonation = data.length ? Math.max(...data.map(d => d.amount)) : 0;
  const avgDonation = data.length ? total / data.length : 0;

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الإيرادات" value={`${(total / 1000).toFixed(0)}K`} icon={TrendingUp} color={C.success} />
        <KPICard title="عدد المعاملات" value={filtered.length} icon={Receipt} color="#7c3aed" />
        <KPICard title="أكبر تبرع" value={`${(maxDonation / 1000).toFixed(0)}K`} icon={Star} color={C.accent} />
        <KPICard title="متوسط التبرع" value={`${(avgDonation / 1000).toFixed(1)}K`} icon={BarChart3} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالوصف أو المتبرع..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              {INCOME_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تسجيل إيراد</Btn>}
          </div>
        </div>
      </Card>

      {loadError && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, marginBottom: 14, background: `${C.danger}10`, border: `1px solid ${C.danger}30`, borderRadius: 10, color: C.danger, fontSize: 13 }}>
          <AlertCircle size={16} /> {loadError}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 24, color: C.textLight, fontSize: 14 }}>
          <RefreshCw size={16} /> جارٍ تحميل الإيرادات...
        </div>
      ) : data.length === 0 && !loadError ? (
        <Card>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 40, color: C.textLight }}>
            <Receipt size={32} color="#ccc" />
            <div style={{ fontSize: 14 }}>لا توجد إيرادات</div>
          </div>
        </Card>
      ) : (
        <>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <DataTable
              columns={[
                { key: "number", label: "الرقم", width: "90px", render: v => <span style={{ fontWeight: 600, color: C.primary }}>{v}</span> },
                { key: "date", label: "التاريخ", width: "100px" },
                { key: "type", label: "النوع", width: "110px" },
                { key: "donor", label: "المتبرع / المصدر" },
                { key: "amount", label: "المبلغ (ريال)", width: "120px", render: v => <span style={{ fontWeight: 700, color: C.success }}>{v.toLocaleString()}</span> },
                { key: "method", label: "طريقة الدفع", width: "110px" },
                { key: "status", label: "الحالة", width: "100px", render: v => <StatusBadge status={v} /> },
              ]}
              data={filtered}
              onRowClick={row => setSelected(row)}
            />
          </Card>

          {/* Total bar */}
          <div style={styles.totalBar}>
            <span>الإجمالي المعروض:</span>
            <span style={{ fontWeight: 700, color: C.success, fontSize: 18 }}>{total.toLocaleString()} ريال</span>
          </div>
        </>
      )}

      {selected && (
        <Modal title={`إيراد — ${selected.number}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الرقم:</strong> {selected.number}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>المتبرع:</strong> {selected.donor}</div>
            <div style={styles.detailRow}><strong>المبلغ:</strong> <span style={{ color: C.success, fontWeight: 700 }}>{selected.amount.toLocaleString()} ريال</span></div>
            <div style={styles.detailRow}><strong>طريقة الدفع:</strong> {selected.method}</div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="تسجيل إيراد جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_INCOME); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={INCOME_TYPES} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <Field label="المتبرع / المصدر" required placeholder="اسم المتبرع أو الجهة" value={form.donor} onChange={v => setForm({ ...form, donor: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المبلغ (ريال)" type="number" required placeholder="0" value={form.amount} onChange={v => setForm({ ...form, amount: v })} />
              <Field label="طريقة الدفع" type="select" options={PAYMENT_METHODS} required value={form.method} onChange={v => setForm({ ...form, method: v })} />
            </div>
            <Field label="الوصف" type="textarea" placeholder="تفاصيل الإيراد..." value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={TrendingUp} variant="success" onClick={handleSave}>تسجيل</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_INCOME); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Expenses Tab — المصروفات
   ───────────────────────────────────────────── */
const INITIAL_EXPENSE = { department: "", date: "", description: "", amount: "", method: "", vendor: "", notes: "" };

function ExpensesTab({ canEdit }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_EXPENSE);
  const [showToast, ToastView] = useToast();

  // تحويل صف Supabase (module=finance) إلى شكل الواجهة.
  // vendor→beneficiary · method→payment_method · department→department (العمود الجديد).
  const mapRow = (row, i) => ({
    id: row.id,
    number: `م-${String(i + 1).padStart(3, "0")}`,
    date: row.txn_date || "",
    department: row.department || "",
    description: row.description || "",
    amount: Number(row.amount) || 0,
    vendor: row.beneficiary || "",
    method: row.payment_method || "",
    status: row.status || "pending",
    notes: row.notes || "",
  });

  async function loadData() {
    setLoading(true);
    setLoadError("");
    try {
      const rows = await getTransactions("finance", "expense");
      setData((rows || []).map(mapRow));
    } catch (e) {
      setLoadError(e?.message || "تعذّر جلب المصروفات");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave() {
    if (!form.department || !form.date || !form.description || !form.amount || !form.method) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    try {
      await createTransaction({
        module: "finance",
        txn_date: form.date,
        description: form.description,
        amount: Number(form.amount) || 0,
        beneficiary: form.vendor || "",
        payment_method: form.method,
        department: form.department,
        status: "pending",
        notes: form.notes || "",
      });
      setForm(INITIAL_EXPENSE);
      setShowAdd(false);
      showToast("تم تسجيل المصروف");
      loadData();
    } catch (e) {
      showToast(e?.message || "تعذّر حفظ المصروف", "error");
    }
  }

  const depts = [...new Set(data.map(d => d.department).filter(Boolean))];
  const filtered = data.filter(d => {
    const matchSearch = d.description.includes(search) || d.vendor.includes(search);
    const matchDept = filterDept === "all" || d.department === filterDept;
    return matchSearch && matchDept;
  });

  const total = filtered.reduce((s, d) => s + d.amount, 0);

  const EXP_STATUSES = [
    { key: "approved", label: "معتمد", color: C.success },
    { key: "pending", label: "بانتظار الاعتماد", color: C.warning },
    { key: "paid", label: "تم الصرف", color: C.teal },
    { key: "rejected", label: "مرفوض", color: C.danger },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المصروفات" value={`${(total / 1000).toFixed(0)}K`} icon={TrendingDown} color={C.danger} />
        <KPICard title="عدد المعاملات" value={filtered.length} icon={Receipt} color="#7c3aed" />
        <KPICard title="بانتظار الاعتماد" value={data.filter(d => d.status === "pending").length} icon={Clock} color={C.warning} />
        <KPICard title="تم الصرف" value={data.filter(d => d.status === "paid").length} icon={CheckCircle} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأقسام</option>
              {depts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تسجيل مصروف</Btn>}
          </div>
        </div>
      </Card>

      {loadError && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, marginBottom: 14, background: `${C.danger}10`, border: `1px solid ${C.danger}30`, borderRadius: 10, color: C.danger, fontSize: 13 }}>
          <AlertCircle size={16} /> {loadError}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 24, color: C.textLight, fontSize: 14 }}>
          <RefreshCw size={16} /> جارٍ تحميل المصروفات...
        </div>
      ) : data.length === 0 && !loadError ? (
        <Card>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 40, color: C.textLight }}>
            <Receipt size={32} color="#ccc" />
            <div style={{ fontSize: 14 }}>لا توجد مصروفات</div>
          </div>
        </Card>
      ) : (
        <>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <DataTable
              columns={[
                { key: "number", label: "الرقم", width: "90px", render: v => <span style={{ fontWeight: 600, color: C.primary }}>{v}</span> },
                { key: "date", label: "التاريخ", width: "100px" },
                { key: "department", label: "القسم", width: "110px" },
                { key: "description", label: "الوصف" },
                { key: "amount", label: "المبلغ (ريال)", width: "120px", render: v => <span style={{ fontWeight: 700, color: C.danger }}>{v.toLocaleString()}</span> },
                { key: "vendor", label: "المورد", width: "130px" },
                { key: "status", label: "الحالة", width: "120px", render: v => <StatusBadge status={v} statuses={EXP_STATUSES} /> },
              ]}
              data={filtered}
              onRowClick={row => setSelected(row)}
            />
          </Card>

          <div style={styles.totalBar}>
            <span>الإجمالي المعروض:</span>
            <span style={{ fontWeight: 700, color: C.danger, fontSize: 18 }}>{total.toLocaleString()} ريال</span>
          </div>
        </>
      )}

      {selected && (
        <Modal title={`مصروف — ${selected.number}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الرقم:</strong> {selected.number}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>القسم:</strong> {selected.department}</div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
            <div style={styles.detailRow}><strong>المبلغ:</strong> <span style={{ color: C.danger, fontWeight: 700 }}>{selected.amount.toLocaleString()} ريال</span></div>
            <div style={styles.detailRow}><strong>المورد:</strong> {selected.vendor}</div>
            <div style={styles.detailRow}><strong>طريقة الدفع:</strong> {selected.method}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="تسجيل مصروف جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_EXPENSE); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="القسم" type="select" options={["الجاليات","المساجد","النسائي","الإدارة","تنمية الموارد","الاتصال","التطوع","الموارد البشرية"]} required value={form.department} onChange={v => setForm({ ...form, department: v })} />
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
            </div>
            <Field label="الوصف" required placeholder="وصف المصروف" value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المبلغ (ريال)" type="number" required placeholder="0" value={form.amount} onChange={v => setForm({ ...form, amount: v })} />
              <Field label="طريقة الدفع" type="select" options={PAYMENT_METHODS} required value={form.method} onChange={v => setForm({ ...form, method: v })} />
            </div>
            <Field label="المورد" placeholder="اسم المورد أو الجهة" value={form.vendor} onChange={v => setForm({ ...form, vendor: v })} />
            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={TrendingDown} variant="danger" onClick={handleSave}>تسجيل</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_EXPENSE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Budgets Tab — الميزانيات
   ───────────────────────────────────────────── */
const BUDGET_DEPTS = ["الجاليات", "المساجد", "النسائي", "تنمية الموارد", "الموارد البشرية", "الاتصال", "التطوع", "الإدارة", "الحوكمة"];
const INITIAL_BUDGET = { department: "", item: "", approvedAmount: "", spentAmount: "", period: "1447", notes: "" };

const BUDGET_COLORS = ["#2563eb", "#0891b2", "#db2777", "#d97706", "#0284c7", "#c026d3", "#059669", "#475569", "#7c3aed"];
function mapBudgetRow(r, i = 0) {
  const department = r.department || "";
  const item = r.item || "";
  return {
    id: r.id,
    name: item ? `${department} — ${item}` : department,
    budget: Number(r.budget) || 0,
    spent: Number(r.spent) || 0,
    color: BUDGET_COLORS[i % BUDGET_COLORS.length],
    department,
    item,
    period: r.period || "",
    notes: r.notes || "",
  };
}

function BudgetsTab({ canEdit }) {
  const [budgets, setBudgets] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_BUDGET);
  const [showToast, ToastView] = useToast();

  const totalBudget = budgets.reduce((s, b) => s + b.budget, 0);
  const totalSpent = budgets.reduce((s, b) => s + b.spent, 0);
  const remainingBudget = totalBudget - totalSpent;
  const spendRatio = totalBudget ? Math.round((totalSpent / totalBudget) * 100) : 0;

  useEffect(() => {
    getBudgets()
      .then(rows => setBudgets((rows || []).map(mapBudgetRow)))
      .catch(() => {});
  }, []);

  async function handleSave() {
    if (!form.department || !form.item || !form.approvedAmount) {
      showToast("القسم والبند والمبلغ المعتمد مطلوبة", "error");
      return;
    }
    const payload = {
      department: form.department,
      item: form.item,
      budget: Number(form.approvedAmount) || 0,
      spent: Number(form.spentAmount) || 0,
      period: form.period,
      notes: form.notes,
    };
    try {
      await createBudget(payload);
      const rows = await getBudgets();
      setBudgets((rows || []).map(mapBudgetRow));
      setForm(INITIAL_BUDGET);
      setShowAdd(false);
      showToast("تمت إضافة بند الميزانية");
    } catch (e) {
      showToast(e?.message || "تعذّر إضافة بند الميزانية", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="الميزانية الإجمالية" value={`${(totalBudget / 1000000).toFixed(2)}M`} icon={Target} color={C.primary} />
        <KPICard title="المنصرف" value={`${(totalSpent / 1000000).toFixed(2)}M`} icon={TrendingDown} color={C.danger} />
        <KPICard title="المتبقي" value={`${(remainingBudget / 1000000).toFixed(2)}M`} icon={DollarSign} color={C.success} />
        <KPICard title="نسبة الإنفاق" value={`${spendRatio}%`} icon={BarChart3} color={C.warning} />
      </div>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة بند ميزانية</Btn>
        </div>
      )}

      <Card style={{ padding: 20 }}>
        <h4 style={styles.chartTitle}><Target size={18} color={C.primary} /> ميزانية الأقسام — 1446هـ</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 16 }}>
          {budgets.map((d, i) => {
            const pct = Math.round((d.spent / d.budget) * 100);
            return (
              <div key={i} style={styles.budgetRow}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, width: 180 }}>
                  <div style={{ ...styles.budgetDot, background: d.color }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>{d.name}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.budgetBarBg}>
                    <div style={{
                      ...styles.budgetBarFill,
                      width: `${pct}%`,
                      background: pct > 80 ? C.danger : pct > 60 ? C.warning : d.color,
                    }} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 13, minWidth: 280 }}>
                  <span style={{ color: C.textLight }}>الميزانية: <strong style={{ color: C.primary }}>{d.budget.toLocaleString()}</strong></span>
                  <span style={{ color: C.textLight }}>المنصرف: <strong style={{ color: C.danger }}>{d.spent.toLocaleString()}</strong></span>
                  <span style={{
                    fontWeight: 700,
                    color: pct > 80 ? C.danger : pct > 60 ? C.warning : C.success,
                  }}>{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {showAdd && (
        <Modal title="إضافة بند ميزانية" onClose={() => { setShowAdd(false); setForm(INITIAL_BUDGET); }} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="القسم" type="select" options={BUDGET_DEPTS} required value={form.department} onChange={v => setForm({ ...form, department: v })} />
            <Field label="البند" required placeholder="مثال: برامج تعليمية" value={form.item} onChange={v => setForm({ ...form, item: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="المبلغ المعتمد (ر.س)" type="number" required placeholder="0" value={form.approvedAmount} onChange={v => setForm({ ...form, approvedAmount: v })} />
              <Field label="المنصرف حتى الآن" type="number" placeholder="0" value={form.spentAmount} onChange={v => setForm({ ...form, spentAmount: v })} />
              <Field label="السنة المالية" placeholder="1447" value={form.period} onChange={v => setForm({ ...form, period: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Target} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_BUDGET); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Accounts Tab — الحسابات البنكية
   ───────────────────────────────────────────── */
function AccountsTab() {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.accountsGrid}>
        {BANK_ACCOUNTS.map((a, i) => (
          <Card key={i} style={styles.accountCard}>
            <div style={styles.accountHeader}>
              <div style={{ ...styles.accountIcon, background: `${a.color}15` }}>
                <Building size={24} color={a.color} />
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.primary }}>{a.bank}</h4>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{a.type} — {a.accountNumber}</p>
              </div>
            </div>
            <div style={styles.accountBalance}>
              <span style={{ fontSize: 12, color: C.textLight }}>الرصيد الحالي</span>
              <span style={{ fontSize: 28, fontWeight: 700, color: a.color }}>{a.balance.toLocaleString()}</span>
              <span style={{ fontSize: 12, color: C.textLight }}>ريال سعودي</span>
            </div>
            <div style={styles.accountMeta}>
              <div style={styles.accountMetaItem}>
                <span style={{ fontSize: 11, color: C.textLight }}>آخر حركة</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>{a.lastTransaction}</span>
              </div>
              <div style={styles.accountMetaItem}>
                <span style={{ fontSize: 11, color: C.textLight }}>الحالة</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.success }}>نشط</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const MONTHLY_DATA = [];
const EXPENSE_DIST = [];
const EXPENSE_COLORS = [C.primary, "#0891b2", "#2563eb", "#ea580c", "#7c3aed", C.textLight];

const BANK_ACCOUNTS = [
  { bank: "البنك الأهلي السعودي", type: "حساب جاري", accountNumber: "SA44 1000 XXXX XXXX XX01", balance: 1250000, lastTransaction: "1446/09/12", color: "#16a34a" },
  { bank: "بنك الراجحي", type: "حساب جاري", accountNumber: "SA44 8000 XXXX XXXX XX02", balance: 850000, lastTransaction: "1446/09/10", color: "#0284c7" },
  { bank: "بنك الإنماء", type: "حساب استثماري", accountNumber: "SA44 0500 XXXX XXXX XX03", balance: 620000, lastTransaction: "1446/08/28", color: "#7c3aed" },
  { bank: "البنك السعودي الفرنسي", type: "حساب وقف", accountNumber: "SA44 5500 XXXX XXXX XX04", balance: 380000, lastTransaction: "1446/07/15", color: "#d97706" },
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  chartsRow: { display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 16 },
  chartTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  legendGrid: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 8 },
  legendItem: { display: "flex", alignItems: "center", gap: 4 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },
  totalBar: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "14px 20px", marginTop: 12, background: C.white, borderRadius: 10,
    border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 500, color: C.primary,
  },

  /* Budgets */
  budgetRow: { display: "flex", alignItems: "center", gap: 16 },
  budgetDot: { width: 12, height: 12, borderRadius: 4, flexShrink: 0 },
  budgetBarBg: { height: 10, borderRadius: 5, background: C.bg, overflow: "hidden" },
  budgetBarFill: { height: "100%", borderRadius: 5, transition: "width 0.5s" },

  /* Accounts */
  accountsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  accountCard: { padding: 24 },
  accountHeader: { display: "flex", alignItems: "center", gap: 20, marginBottom: 20 },
  accountIcon: { width: 52, height: 52, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  accountBalance: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "20px 0", borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` },
  accountMeta: { display: "flex", justifyContent: "space-around", marginTop: 16 },
  accountMetaItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
};
